import { isStorageLimitError } from "../../../utils/storage-limit-utils";

const ACQUIRE_BEFORE_PLACE_TABS = new Set(["libraries", "public-domain-art"]);
const PUBLIC_DOMAIN_ART_ASSET_CATEGORY = "digital-art";

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function firstNonEmptyString(values) {
  return values.find(isNonEmptyString)?.trim() || "";
}

function isSketchfabAsset(asset) {
  if (!asset) return false;

  const source = firstNonEmptyString([asset?.source, asset?.provider]).toLowerCase();
  const url = firstNonEmptyString([asset?.url, asset?.artwork?.artwork_model]).toLowerCase();
  const museum = firstNonEmptyString([asset?.artwork?.museum, asset?.metadata?.artwork?.museum]).toLowerCase();

  return source === "sketchfab" || url.includes("sketchfab.com") || museum === "sketchfab";
}

function normalizePublicDomainArtworkMetadata(artwork, asset) {
  if (!artwork || typeof artwork !== "object" || Array.isArray(artwork)) {
    return artwork;
  }

  const hasExistingMetadata =
    artwork.metadata && typeof artwork.metadata === "object" && !Array.isArray(artwork.metadata);
  const metadata =
    hasExistingMetadata
      ? { ...artwork.metadata }
      : {};

  const normalizedArtwork = { ...artwork };

  const normalizedMedium = firstNonEmptyString([
    artwork.medium,
    artwork.category,
    metadata.customMedium,
    metadata.medium?.name,
    metadata.category,
    asset?.medium,
    asset?.artwork?.medium,
    asset?.artwork?.category
  ]);

  if (normalizedMedium) {
    if (!isNonEmptyString(normalizedArtwork.medium)) {
      normalizedArtwork.medium = normalizedMedium;
    }

    if (!isNonEmptyString(metadata.customMedium)) {
      metadata.customMedium = normalizedMedium;
    }
  }

  if (hasExistingMetadata || Object.keys(metadata).length > 0) {
    normalizedArtwork.metadata = metadata;
  }

  return normalizedArtwork;
}

export { isStorageLimitError };

export function canAddLibraryAsset({ selectedTab, workspace }) {
  const isExternalAssetTab = ACQUIRE_BEFORE_PLACE_TABS.has(selectedTab);
  return (
    isExternalAssetTab &&
    workspace?.authorization?.projection_status === "ready" &&
    Array.isArray(workspace.authorization.permissions) &&
    workspace.authorization.permissions.includes("asset:create")
  );
}

export function shouldAcquireBeforePlace(selectedTab) {
  return ACQUIRE_BEFORE_PLACE_TABS.has(selectedTab);
}

export function extractArtworkMetadata(asset) {
  return asset?.metadata?.artwork || asset?.artwork || null;
}

export async function resolveAcquireUrl({ asset, selectedTab, fetchImpl = fetch }) {
  const baseUrl = asset?.url || "";
  if (selectedTab !== "public-domain-art") {
    return baseUrl;
  }

  const artwork = extractArtworkMetadata(asset);
  const museum = firstNonEmptyString([artwork?.museum, asset?.museum]).toLowerCase();

  const knownMetImageUrl = firstNonEmptyString([
    asset?.image,
    artwork?.image,
    asset?.artwork?.image,
    asset?.metadata?.artwork?.image
  ]);
  if (museum === "met" && knownMetImageUrl) {
    return knownMetImageUrl;
  }

  if (museum !== "met" || !isNonEmptyString(baseUrl)) {
    return baseUrl;
  }

  try {
    const response = await fetchImpl(baseUrl);
    if (!response.ok) {
      return baseUrl;
    }

    const json = await response.json();
    const resolved = firstNonEmptyString([
      json?.data?.url,
      json?.data?.image,
      json?.url,
      json?.image
    ]);

    return resolved || baseUrl;
  } catch (error) {
    return baseUrl;
  }
}

export function buildAcquirePayload({ asset, forceDuplicate, selectedTab, urlOverride = "" }) {
  const shouldForceDigitalArtCategory = selectedTab === "public-domain-art" || isSketchfabAsset(asset);
  const payload = {
    url: urlOverride || asset?.url || "",
    title: asset?.title || asset?.name || "",
    force_duplicate: !!forceDuplicate
  };

  if (shouldForceDigitalArtCategory) {
    payload.asset_category = PUBLIC_DOMAIN_ART_ASSET_CATEGORY;
  }

  if (shouldForceDigitalArtCategory) {
    const artwork = extractArtworkMetadata(asset);
    if (artwork) {
      const baseArtwork =
        selectedTab === "public-domain-art" ? normalizePublicDomainArtworkMetadata(artwork, asset) : { ...artwork };
      const baseArtworkMetadata =
        baseArtwork?.metadata && typeof baseArtwork.metadata === "object"
          ? baseArtwork.metadata
          : {};

      payload.metadata = {
        artwork: {
          ...baseArtwork,
          asset_category: PUBLIC_DOMAIN_ART_ASSET_CATEGORY,
          metadata: {
            ...baseArtworkMetadata,
            asset_category: PUBLIC_DOMAIN_ART_ASSET_CATEGORY,
            declareRights: true,
            terms: true
          }
        }
      };
    }
  }

  return payload;
}

export function shouldShowMenuTrigger({ canRename, canDelete, canOptimize, canAddToLibrary }) {
  return !!(canRename || canDelete || canOptimize || canAddToLibrary);
}
